import { Link, NavLink, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { C2Button } from './c2';
import { useCurrentUser } from '../lib/useCurrentUser';
import type { User } from '../lib/api';

const navItems = [
  { to: '/#how', label: 'How it works' },
  { to: '/demo', label: 'Demo' },
  { to: '/compare', label: 'Compare' },
  { to: '/extension', label: 'Extension' },
  { to: '/faq', label: 'FAQ' },
];

function Logo() {
  return (
    <Link to="/" className="flex items-center gap-3 no-underline">
      <div
        className="w-[38px] h-[38px] rounded-[12px] flex items-center justify-center font-mono font-bold text-sm"
        style={{ background: 'var(--accent-soft)', color: 'var(--accent-deep)' }}
      >
        go/
      </div>
      <span className="text-[17px] font-bold tracking-[-0.01em] text-ink">Tidy Go Links</span>
    </Link>
  );
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  return parts.slice(0, 2).map(p => p[0]).join('').toUpperCase();
}

function UserPill({ user }: { user: User }) {
  return (
    <Link
      to="/dashboard"
      className="flex items-center gap-2.5 no-underline transition-colors hover:bg-surface"
      style={{ padding: '4px 12px 4px 4px', borderRadius: 999 }}
    >
      <div
        className="flex items-center justify-center font-semibold text-[13px]"
        style={{
          width: 32,
          height: 32,
          borderRadius: '50%',
          background: 'var(--accent-soft)',
          color: 'var(--accent-deep)',
        }}
      >
        {initials(user.name)}
      </div>
      <div className="flex flex-col leading-tight text-left">
        <span className="text-[13px] font-semibold text-ink">{user.name}</span>
        <span className="text-[11px] text-ink-soft">{user.email}</span>
      </div>
    </Link>
  );
}

function Nav() {
  const location = useLocation();
  const auth = useCurrentUser();
  const isActive = (to: string) => {
    const path = to.split('#')[0];
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <nav
      className="flex justify-between items-center sticky top-0 z-50 backdrop-blur-md border-b border-hairline"
      style={{
        padding: '22px clamp(32px, 6vw, 96px)',
        background: 'color-mix(in oklch, var(--color-bg) 93%, transparent)',
      }}
    >
      <Logo />
      <div className="flex gap-7 items-center text-[15px] text-ink-soft font-medium">
        {navItems.map(item => (
          <NavLink
            key={item.to}
            to={item.to}
            className={[
              'no-underline transition-colors',
              isActive(item.to) ? 'text-ink font-semibold' : 'text-ink-soft font-medium',
            ].join(' ')}
          >
            {item.label}
          </NavLink>
        ))}
        {auth.status === 'signed-out' && (
          <>
            <Link to="/login" className="text-ink no-underline font-medium">
              Sign in
            </Link>
            <Link to="/gatekeeper">
              <C2Button variant="primary" size="md">
                Get started
              </C2Button>
            </Link>
          </>
        )}
        {auth.status === 'signed-in' && auth.user && <UserPill user={auth.user} />}
      </div>
    </nav>
  );
}

interface FooterCol {
  title: string;
  links: Array<{ label: string; to: string; external?: boolean }>;
}

const footerCols: FooterCol[] = [
  {
    title: 'Product',
    links: [
      { label: 'Extension', to: '/extension' },
      { label: 'Compare', to: '/compare' },
      { label: 'How it works', to: '/#how' },
    ],
  },
  {
    title: 'Resources',
    links: [
      { label: 'FAQ', to: '/faq' },
      { label: 'Support', to: '/support' },
      { label: 'Handoff (dev)', to: 'https://github.com/freecodecollective/theoriginalgolinks/tree/main/design_handoff_tidy_go_links_v2', external: true },
      { label: 'Privacy', to: '/privacy', external: true },
      { label: 'Terms', to: '/terms', external: true },
    ],
  },
  {
    title: 'Company',
    links: [
      { label: 'About', to: '/about' },
      { label: 'GitHub', to: 'https://github.com/freecodecollective/theoriginalgolinks', external: true },
      { label: 'Free Code Collective', to: 'https://freecodecollective.org', external: true },
    ],
  },
];

function Footer() {
  return (
    <footer
      className="bg-surface border-t border-hairline"
      style={{ padding: '72px clamp(32px, 6vw, 96px) 32px' }}
    >
      <div className="max-w-[1280px] mx-auto">
        <div className="grid grid-cols-[1.5fr_1fr_1fr_1fr] gap-12 mb-14">
          <div>
            <div className="mb-4">
              <Logo />
            </div>
            <p className="text-sm text-ink-soft leading-[1.6] max-w-[280px] m-0">
              Short links your team can actually remember. Free, open source, and genuinely easy.
            </p>
          </div>
          {footerCols.map(col => (
            <div key={col.title}>
              <h4 className="text-[13px] font-bold text-ink mb-4 tracking-wider uppercase">
                {col.title}
              </h4>
              <div className="flex flex-col gap-2.5">
                {col.links.map(link =>
                  link.external ? (
                    <a
                      key={link.label}
                      href={link.to}
                      className="text-sm text-ink-soft font-medium no-underline hover:text-ink transition-colors text-left"
                    >
                      {link.label}
                    </a>
                  ) : (
                    <Link
                      key={link.label}
                      to={link.to}
                      className="text-sm text-ink-soft font-medium no-underline hover:text-ink transition-colors text-left"
                    >
                      {link.label}
                    </Link>
                  ),
                )}
              </div>
            </div>
          ))}
        </div>
        <div className="flex justify-between items-center pt-6 border-t border-hairline text-[13px] text-ink-muted">
          <span>
            © 2026 Tidy Go Links is brought to you by Tidy Software LLC and Free Code Collective
          </span>
          <span>
            Made with <span style={{ color: 'var(--accent-solid)' }}>♥</span> by ex-Googlers, ex-Meta, and friends
          </span>
        </div>
      </div>
    </footer>
  );
}

export default function Shell({ children }: { children: ReactNode }) {
  return (
    <>
      <Nav />
      <main>{children}</main>
      <Footer />
    </>
  );
}
